/**
 * FAQ completa da Kentro — usada em /perguntas-frequentes.
 * Estruturada em grupos para navegação + JSON-LD FAQPage.
 * Conteúdo aprovado pela área de marketing/produto (2026).
 */

export type FaqItem = {
  id: string;
  q: string;
  a: string;
  keywords?: string;
};

export type FaqGroup = {
  id: string;
  title: string;
  description?: string;
  items: FaqItem[];
};

export const FAQ_GROUPS: FaqGroup[] = [
  {
    id: "sobre-a-kentro",
    title: "Sobre a Kentro",
    description: "O que é, para que serve e como a plataforma se posiciona.",
    items: [
      {
        id: "o-que-e-a-kentro",
        q: "O que é a Kentro?",
        a: "A Kentro é uma plataforma brasileira omnichannel com Inteligência Artificial para Atendimento, Vendas e Marketing. Unifica WhatsApp (API Oficial da Meta), Instagram, Facebook, WebChat e outros canais em uma única tela, com CRM integrado, assistentes de IA, automações e relatórios completos. Hoje mais de 1.600 empresas e 14.670 agentes usam a Kentro para escalar resultados sem aumentar a equipe. A plataforma é organizada em 4 grandes áreas: Operação de Atendimento, CRM e Tarefas, Relatórios e Configurações.",
        keywords: "Kentro, atendimento omnichannel, CRM para WhatsApp, IA para atendimento, plataforma de atendimento digital, 1600 empresas",
      },
      {
        id: "quatro-modulos-kentro",
        q: "Como a plataforma Kentro é organizada?",
        a: "A Kentro se divide em 4 módulos: (1) Operação de Atendimento — dashboard em tempo real, central de chat omnichannel, chat interno e tarefas do agente; (2) CRM e Funis de Vendas — funis Kanban, configurações por funil (geral, permissões e automações), templates de documentos e formulários; (3) Relatórios — KPIs por agente, dashboards de CRM e tickets, histórico de atendimentos e oportunidades, relatórios de filas, tempos e agenda de contatos; (4) Configurações — filas/canais, usuários, pausas, campanhas, pesquisas, assistentes de IA, automação, cadastros, etiquetas, grupos, tickets, monitoramento de produtividade e log de auditoria.",
        keywords: "módulos Kentro, estrutura da plataforma, operação atendimento, CRM funis, relatórios, configurações",
      },

      {
        id: "para-que-serve-a-kentro",
        q: "Para que serve a Kentro?",
        a: "A Kentro serve para organizar o atendimento, acelerar vendas e reduzir perdas causadas por demora, falta de histórico e conversas espalhadas. A plataforma ajuda empresas a centralizar canais, distribuir atendimentos, acompanhar leads no funil de vendas, automatizar respostas, criar agentes de IA e medir indicadores como tempo de resposta, conversão, produtividade e satisfação.",
        keywords: "gestão de atendimento, atendimento digital, automação de atendimento, funil de vendas, atendimento no WhatsApp",
      },
      {
        id: "crm-atendimento-ou-ia",
        q: "A Kentro é um CRM, uma plataforma de atendimento ou uma IA?",
        a: "A Kentro une os três em uma única solução: Chat + CRM + IA. O chat omnichannel centraliza os canais de atendimento, o CRM organiza oportunidades, histórico e follow-ups, e a IA atua para responder dúvidas, qualificar leads, apoiar vendas e transferir para humanos quando necessário. Essa combinação evita que a empresa precise usar várias ferramentas desconectadas.",
        keywords: "Chat CRM IA, CRM integrado ao WhatsApp, plataforma omnichannel com IA, software de atendimento com CRM",
      },
    ],
  },
  {
    id: "whatsapp-api-oficial",
    title: "WhatsApp e API Oficial",
    description: "Como a Kentro opera com a API Oficial do WhatsApp / Meta.",
    items: [
      {
        id: "api-oficial-whatsapp",
        q: "A Kentro usa API Oficial do WhatsApp?",
        a: "Sim. A Kentro trabalha com a API Oficial do WhatsApp / Meta, permitindo que empresas usem o WhatsApp de forma profissional, com mais segurança, múltiplos atendentes, automações, templates, histórico centralizado, integração com CRM e possibilidade de operação em escala. A Kentro também auxilia empresas no processo de estruturação e adaptação ao uso da API Oficial.",
        keywords: "API Oficial do WhatsApp, WhatsApp Business API, Meta WhatsApp",
      },
      {
        id: "business-vs-api-oficial",
        q: "Qual a diferença entre WhatsApp Business comum e API Oficial do WhatsApp?",
        a: "O WhatsApp Business comum é indicado para operações pequenas, com baixo volume e poucos atendentes. Já a API Oficial do WhatsApp é indicada para empresas que precisam escalar atendimento, usar múltiplos usuários, integrar CRM, automatizar mensagens, criar filas, medir indicadores e manter controle profissional sobre conversas e dados. Com a Kentro, a API Oficial fica conectada a atendimento omnichannel, CRM, IA e relatórios.",
        keywords: "WhatsApp Business vs API Oficial, múltiplos atendentes WhatsApp, CRM WhatsApp",
      },
      {
        id: "evitar-banimento",
        q: "A Kentro ajuda a evitar banimento de WhatsApp?",
        a: "A Kentro ajuda empresas a operarem de forma mais profissional usando a API Oficial do WhatsApp e boas práticas de envio, templates e atendimento. Isso reduz riscos comuns de operações improvisadas, como uso inadequado de números, disparos sem estratégia e falta de controle de qualidade. Mesmo assim, a reputação do número, a qualidade das mensagens e a política da Meta precisam ser respeitadas.",
        keywords: "evitar banimento WhatsApp, WhatsApp corporativo seguro",
      },
      {
        id: "cobranca-api-oficial",
        q: "Como funciona a cobrança da API Oficial do WhatsApp?",
        a: "A cobrança da API Oficial do WhatsApp depende das regras da Meta e pode variar conforme categoria de mensagem, país do destinatário e tipo de template utilizado. Desde julho de 2025, o mercado passou a trabalhar com modelo de cobrança por mensagens/template entregues em diferentes categorias, como marketing, utilidade e autenticação. A Kentro ajuda sua empresa a entender esses custos, configurar templates corretamente e economizar com uma estratégia de atendimento mais inteligente.",
        keywords: "preço WhatsApp API, cobrança WhatsApp Business API, custo API Oficial WhatsApp",
      },
      {
        id: "cobranca-nao-entregue",
        q: "A Kentro cobra quando a mensagem não é entregue?",
        a: "A cobrança das mensagens da API Oficial segue as regras da Meta e, no modelo atual, o custo costuma estar associado à entrega de templates/mensagens cobradas conforme categoria e país. A Kentro ajuda sua empresa a acompanhar o uso, reduzir desperdícios e evitar envios desnecessários com segmentação, automação e estratégia de comunicação.",
        keywords: "cobrança por template entregue, mensagem entregue WhatsApp API",
      },
      {
        id: "templates-whatsapp",
        q: "O que são templates de WhatsApp?",
        a: "Templates de WhatsApp são modelos de mensagens previamente aprovados pela Meta para iniciar conversas ou enviar comunicações ativas, como avisos, cobranças, confirmações, campanhas, lembretes e autenticações. Com a Kentro, sua empresa pode organizar o uso de templates dentro da operação comercial e de atendimento, mantendo controle sobre campanhas, custos e qualidade das mensagens.",
        keywords: "templates WhatsApp, modelos de mensagem WhatsApp, campanha WhatsApp API",
      },
      {
        id: "trocar-numero-whatsapp",
        q: "Preciso trocar meu número de WhatsApp para usar a Kentro?",
        a: "Não necessariamente. Em muitos casos, é possível usar o número da empresa na API Oficial, desde que ele atenda aos requisitos técnicos e comerciais da Meta. A Kentro pode orientar o processo de avaliação, migração e ativação para que a empresa use o WhatsApp de forma profissional.",
        keywords: "migrar número WhatsApp API, usar mesmo número WhatsApp API",
      },
      {
        id: "varios-atendentes",
        q: "A Kentro permite atendimento com vários usuários no mesmo WhatsApp?",
        a: "Sim. Com a API Oficial do WhatsApp conectada à Kentro, a empresa pode organizar múltiplos usuários, filas, departamentos e responsáveis pelo atendimento. Isso evita que tudo dependa de um único celular e permite uma operação mais profissional, com histórico e controle de conversas.",
        keywords: "vários atendentes no WhatsApp, WhatsApp multiusuário, filas WhatsApp",
      },
      {
        id: "disparo-em-massa",
        q: "A Kentro faz disparo em massa?",
        a: "A Kentro trabalha com comunicação profissional via API Oficial e boas práticas da Meta. O foco não é “disparo em massa” sem critério, mas campanhas estruturadas, segmentadas e mensuráveis, respeitando regras de qualidade, consentimento, categorias de templates e experiência do cliente.",
        keywords: "disparo WhatsApp com API Oficial, campanha segmentada WhatsApp",
      },
    ],
  },
  {
    id: "omnichannel-e-canais",
    title: "Omnichannel e canais",
    items: [
      {
        id: "atendimento-omnichannel",
        q: "A Kentro tem atendimento omnichannel?",
        a: "Sim. A Kentro centraliza vários canais em uma única plataforma, como WhatsApp, Instagram, Facebook, e-mail, voz, webchat e outros canais integrados. Isso permite que o cliente comece uma conversa em um canal e continue em outro sem perder histórico, contexto ou qualidade no atendimento. O próprio site da Kentro posiciona a solução como omnichannel com WhatsApp API Oficial, Instagram, voz, e-mail e webchat em uma única caixa de entrada.",
        keywords: "atendimento omnichannel, plataforma omnichannel, central de atendimento omnichannel",
      },
      {
        id: "multicanal-vs-omnichannel",
        q: "Qual a diferença entre multicanal e omnichannel?",
        a: "No multicanal, a empresa atende em vários canais, mas cada canal funciona separado. No omnichannel, todos os canais ficam conectados, com histórico, contexto e continuidade. Isso evita que o cliente precise repetir a mesma história para vários atendentes e permite uma experiência mais organizada do primeiro contato ao pós-venda.",
        keywords: "multicanal vs omnichannel, estratégia omnichannel, canais integrados",
      },
      {
        id: "webchat-para-site",
        q: "A Kentro tem webchat para site?",
        a: "Sim. A Kentro pode fazer parte da estratégia de atendimento digital do site, conectando canais como webchat, WhatsApp e CRM. Isso ajuda a transformar visitantes do site em conversas, leads e oportunidades acompanhadas pelo time comercial.",
        keywords: "webchat para site, chat no site, converter visitantes em leads",
      },
      {
        id: "whatsapp-instagram-facebook",
        q: "A Kentro integra WhatsApp, Instagram e Facebook?",
        a: "Sim. A Kentro centraliza canais como WhatsApp, Instagram, Facebook, e-mail, voz e webchat. Isso permite que sua equipe responda mensagens de diferentes origens em uma única plataforma, com histórico do cliente e mais controle sobre a operação.",
        keywords: "WhatsApp Instagram Facebook atendimento, inbox omnichannel",
      },
      {
        id: "distribuir-atendimentos",
        q: "A Kentro permite distribuir atendimentos automaticamente?",
        a: "Sim. A Kentro pode organizar a distribuição de atendimentos por filas, departamentos, regras, origem do lead, tipo de solicitação ou disponibilidade da equipe. Isso melhora o tempo de resposta e evita que conversas importantes fiquem esquecidas.",
        keywords: "distribuição automática de atendimento, fila de atendimento WhatsApp, roteamento de conversas",
      },
      {
        id: "historico-conversas",
        q: "A Kentro registra o histórico das conversas?",
        a: "Sim. A Kentro centraliza o histórico das interações para que a equipe tenha contexto antes de responder. Isso evita que o cliente precise repetir a mesma história e ajuda vendedores, atendentes e gestores a acompanharem a jornada completa.",
        keywords: "histórico de atendimento, histórico WhatsApp CRM, contexto do cliente",
      },
      {
        id: "chat-interno",
        q: "A Kentro tem chat interno entre os agentes?",
        a: "Sim. A Kentro possui um chat interno para a equipe conversar de forma privada dentro da própria plataforma — individualmente ou em grupos por setor (ex.: Suporte, Comercial). O agente tira dúvidas com um supervisor sem sair da tela de atendimento e sem misturar assuntos de trabalho no WhatsApp pessoal. Os grupos são configurados em Configurações > Grupos > Grupos de Chat Interno.",
        keywords: "chat interno, comunicação interna equipe, atendimento colaborativo",
      },
      {
        id: "transferir-conversa",
        q: "Consigo transferir uma conversa para outro agente ou fila?",
        a: "Sim. Atendimentos podem ser transferidos entre agentes e filas mantendo todo o histórico da conversa. Também é possível travar o atendimento para impedir que outro agente assuma uma conversa em andamento.",
        keywords: "transferir atendimento, transferir fila, travar atendimento",
      },
      {
        id: "webchat-elemento-web",
        q: "Como coloco o chat da Kentro no meu site?",
        a: "Em Configurações > Filas/Canais crie uma fila de WebChat e, na aba Elemento Web, copie o script incorporável e cole no código do seu site. Visitantes conversam pelo widget e as mensagens caem na mesma central de atendimentos, com distribuição automática para os agentes.",
        keywords: "webchat site, widget de chat, elemento web, chat online",
      },
    ],
  },
  {
    id: "crm-vendas",
    title: "CRM, vendas e pós-venda",
    items: [
      {
        id: "crm-integrado",
        q: "A Kentro tem CRM integrado?",
        a: "Sim. A Kentro possui CRM integrado para organizar leads, oportunidades, histórico de atendimento, funil de vendas, follow-ups, etiquetas e tarefas. Isso ajuda equipes comerciais e de atendimento a saberem quem é o cliente, em qual etapa ele está, qual foi a última conversa e qual próximo passo precisa ser feito.",
        keywords: "CRM integrado, CRM para WhatsApp, funil de vendas WhatsApp",
      },
      {
        id: "substitui-crm-tradicional",
        q: "A Kentro substitui um CRM tradicional?",
        a: "Em muitos casos, sim. Para empresas que vendem e atendem principalmente pelo WhatsApp e canais digitais, a Kentro pode substituir ferramentas separadas de atendimento, CRM e automação. A diferença é que a Kentro já nasce conectada às conversas, ao histórico do cliente, aos canais de atendimento e à IA, reduzindo retrabalho e perda de contexto.",
        keywords: "melhor CRM para WhatsApp, alternativa a CRM tradicional, CRM omnichannel, CRM com IA",
      },
      {
        id: "vender-mais-whatsapp",
        q: "A Kentro ajuda a vender mais pelo WhatsApp?",
        a: "Sim. A Kentro ajuda a vender mais pelo WhatsApp porque organiza a jornada do lead: primeiro atendimento, qualificação, histórico, funil, follow-up, automações e transbordo para o vendedor. Em vez de depender de conversas soltas, a empresa passa a ter processo, indicadores e previsibilidade comercial.",
        keywords: "vender mais pelo WhatsApp, vendas WhatsApp, CRM WhatsApp vendas",
      },
      {
        id: "pos-venda",
        q: "A Kentro ajuda no pós-venda?",
        a: "Sim. A Kentro pode ser usada em todo o ciclo do cliente, incluindo atendimento inicial, vendas, suporte, cobrança, recompra, retenção e pós-venda. Com histórico centralizado e CRM, a empresa consegue acompanhar cada cliente com mais contexto e menos retrabalho.",
        keywords: "pós-venda WhatsApp, relacionamento com cliente, retenção de clientes",
      },
      {
        id: "recuperar-clientes",
        q: "A Kentro ajuda a recuperar clientes que não responderam?",
        a: "Sim. Com CRM, histórico, etiquetas, automações e follow-ups, a Kentro ajuda a identificar leads parados, clientes sem retorno e oportunidades esquecidas. Isso permite criar ações de retomada e reduzir perda de vendas por falta de acompanhamento.",
        keywords: "recuperar leads, follow-up WhatsApp, leads parados",
      },
      {
        id: "suporte-e-vendas",
        q: "A Kentro pode ser usada por suporte e vendas ao mesmo tempo?",
        a: "Sim. A Kentro pode organizar diferentes áreas da empresa, como vendas, suporte, financeiro, cobrança, pós-venda e relacionamento. Cada equipe pode ter filas, regras, permissões e indicadores próprios, mantendo o histórico centralizado do cliente.",
        keywords: "vendas e suporte WhatsApp, atendimento por departamento, CRM suporte vendas",
      },
      {
        id: "automacao-cobranca",
        q: "A Kentro permite automação de cobrança?",
        a: "Sim. Dependendo das integrações e regras da operação, a Kentro pode ajudar em automações de cobrança, envio de lembretes, segunda via, boleto e comunicação pelo WhatsApp. Isso é especialmente útil para empresas com alto volume de clientes, como provedores de internet, serviços recorrentes, clínicas e escolas.",
        keywords: "cobrança automática WhatsApp, boleto WhatsApp, segunda via WhatsApp",
      },
      {
        id: "funis-kanban",
        q: "Como funcionam os funis de vendas Kanban da Kentro?",
        a: "O CRM da Kentro organiza oportunidades em funis visuais (Kanban), onde cada oportunidade é um cartão que avança por estágios personalizáveis (ex.: Novo Lead → Em negociação → Proposta enviada → Ganho/Perda). A empresa pode ter múltiplos funis (um por produto, unidade ou equipe) e alternar entre eles pelo seletor no topo da tela. Cada funil tem configuração própria de tempo máximo, permissões por grupo de acesso, automações de ganho/perda/atraso, formulários e templates de documentos.",
        keywords: "funil Kanban, funis de vendas, CRM visual, estágios do funil, múltiplos funis",
      },
      {
        id: "automacao-ganho-perda",
        q: "O que acontece quando marco uma oportunidade como ganha ou perdida?",
        a: "Além de alimentar os relatórios e o Histórico de Oportunidades, o ganho ou perda pode disparar automações configuradas no funil: no ganho, enviar mensagem de boas-vindas, gerar contrato pelo template de documento ou mover o cliente para o funil de pós-venda; na perda, disparar pesquisa de motivo. O funil também tem automação de atraso — se uma oportunidade fica parada além do tempo máximo definido, o gestor recebe alerta automaticamente.",
        keywords: "automação ganho perda, oportunidade ganha, automação de atraso, funil automático",
      },
      {
        id: "gerar-contratos-propostas",
        q: "A Kentro gera contratos e propostas automaticamente?",
        a: "Sim. Em Configurações > Cadastros você cria Modelos de Documentos vinculados aos funis do CRM. Ao gerar o documento a partir de uma oportunidade, os dados do contato e da negociação preenchem automaticamente a proposta ou contrato — sem digitação manual. Também é possível anexar Formulários personalizados (ex.: ficha de qualificação) às oportunidades do funil.",
        keywords: "gerar contrato automático, proposta comercial WhatsApp, modelo de documento CRM",
      },
      {
        id: "reabertura-agendada",
        q: "O cliente pediu para retomar depois — a Kentro lembra sozinha?",
        a: "Sim, com a Reabertura Agendada. Se o cliente diz “só posso decidir dia 10”, o agente agenda a reabertura e a Kentro devolve o atendimento à fila automaticamente na data certa. A lista completa fica em Relatórios > Reaberturas Agendadas.",
        keywords: "reabertura agendada, retomar atendimento, follow-up automático",
      },
    ],
  },
  {
    id: "inteligencia-artificial",
    title: "Inteligência artificial",
    items: [
      {
        id: "ia-para-atendimento",
        q: "A Kentro tem inteligência artificial para atendimento?",
        a: "Sim. A Kentro oferece agentes de IA treinados para o negócio da empresa. A IA pode responder dúvidas frequentes, qualificar leads, apoiar vendas, fazer triagens, consultar informações, reduzir fila de atendimento e transferir para um atendente humano quando necessário. A página de IA da Kentro informa que cada IA pode ser treinada com vocabulário, objeções e fluxo de vendas de cada segmento.",
        keywords: "IA para atendimento, chatbot com inteligência artificial, agente de IA WhatsApp, IA para vendas",
      },
      {
        id: "ia-substitui-humano",
        q: "A IA da Kentro substitui o atendimento humano?",
        a: "Não. A IA da Kentro foi criada para aumentar a produtividade da equipe, não para eliminar o atendimento humano. Ela resolve dúvidas comuns, faz triagens, qualifica leads e organiza informações. Quando identifica uma situação complexa, sensível ou com maior intenção de compra, pode transferir para o atendente certo com o contexto da conversa.",
        keywords: "IA com transbordo humano, atendimento humano com IA, chatbot humanizado",
      },
      {
        id: "ia-treinada-empresa",
        q: "A IA da Kentro pode ser treinada com os dados da minha empresa?",
        a: "Sim. A IA pode ser treinada com informações da empresa, como documentos, FAQs, páginas do site, regras comerciais, preços, políticas, catálogos, objeções, fluxos de venda e conteúdos internos permitidos. Isso faz com que a IA responda de forma mais alinhada ao tom de voz, aos produtos e às regras do seu negócio.",
        keywords: "IA treinada para empresa, base de conhecimento IA, chatbot treinado, IA personalizada",
      },
      {
        id: "atendimento-fora-horario",
        q: "A Kentro funciona fora do horário comercial?",
        a: "Sim. Com automações e IA, a Kentro pode responder dúvidas frequentes, captar informações, qualificar leads e encaminhar solicitações mesmo fora do horário comercial. Quando necessário, o atendimento pode ser transferido para um humano no próximo horário disponível, já com o contexto organizado.",
        keywords: "atendimento 24 horas, IA 24/7, WhatsApp fora do horário",
      },
      {
        id: "abas-do-assistente-ia",
        q: "Como é o editor de um assistente de IA da Kentro?",
        a: "Cada assistente de IA tem um editor com as abas: Geral (identidade, personalidade e instruções), Conhecimento (documentos e conteúdo da empresa que o assistente usa para responder), Procedimentos (roteiros passo a passo), Funções (ações que ele executa, como consultar ou registrar dados), Conectores (integrações com sistemas externos), MCP (Model Context Protocol para ampliar capacidades), Avançado (parâmetros técnicos) e Debug (ambiente de teste). Depois de criado, o assistente é vinculado a uma fila pela aba Configurações de IA da fila.",
        keywords: "assistente IA Kentro, editor de assistente, MCP, debug IA, conhecimento IA",
      },
      {
        id: "mcp-kentro",
        q: "O que é MCP na Kentro?",
        a: "MCP é o Model Context Protocol — um padrão aberto que permite conectar o assistente de IA da Kentro a ferramentas e sistemas externos, ampliando o que ele consegue consultar e executar (ex.: buscar dados em um ERP, acionar um serviço). Fica disponível na aba MCP do editor de assistentes.",
        keywords: "MCP, Model Context Protocol, IA com ferramentas externas",
      },
      {
        id: "testar-ia-antes",
        q: "Consigo testar a IA antes de ativá-la para clientes reais?",
        a: "Sim. A aba Debug do editor de assistente permite conversar com a IA em ambiente de teste e ajustar comportamento, respostas, procedimentos e funções antes de vinculá-la a uma fila em produção.",
        keywords: "testar IA, debug assistente, homologação chatbot",
      },
    ],
  },
  {
    id: "integracoes",
    title: "Integrações e ecossistema",
    items: [
      {
        id: "integracoes-erp-crm",
        q: "A Kentro integra com meu ERP, CRM ou sistema interno?",
        a: "Sim. A Kentro possui integrações nativas e também permite integrações personalizadas por API REST, Webhooks e SDKs. A página de integrações da Kentro cita integrações com sistemas de ISP, CRMs, ferramentas de marketing, produtividade, e-commerce, pagamentos, comunicação e OpenAI/GPT.",
        keywords: "integrações Kentro, API REST, Webhooks, integrar WhatsApp ao ERP",
      },
      {
        id: "quais-integracoes",
        q: "Quais integrações a Kentro possui?",
        a: "A Kentro pode se integrar a ferramentas como IXCSoft, Hubsoft, RadiusNet, RD Station, HubSpot, Pipedrive, Bitrix24, Google Sheets, Trello, ClickUp, Pipefy, Shopify, Hotmart, Kiwify, Asaas, Gmail, Facebook Lead Ads, OpenAI/GPT e outras soluções. Também é possível criar integrações personalizadas para sistemas próprios, ERPs legados ou plataformas específicas.",
        keywords: "integrações WhatsApp, integrações CRM, Kentro API, automação com Webhook",
      },
      {
        id: "integrar-rd-hubspot-pipedrive",
        q: "A Kentro pode ser conectada ao RD Station, HubSpot ou Pipedrive?",
        a: "Sim. A Kentro possui recursos de integração com ferramentas de CRM e marketing, incluindo soluções como RD Station, HubSpot e Pipedrive, além de permitir conexões por API e Webhooks. Isso ajuda a manter atendimento, marketing e vendas sincronizados.",
        keywords: "integrar RD Station WhatsApp, HubSpot WhatsApp, Pipedrive WhatsApp",
      },
      {
        id: "campanhas-marketing-whatsapp",
        q: "A Kentro ajuda campanhas de marketing no WhatsApp?",
        a: "Sim. A Kentro ajuda empresas a usar o WhatsApp de forma estratégica em campanhas, com templates aprovados, segmentação, organização de contatos, acompanhamento de respostas e integração com o atendimento. Isso permite transformar campanhas em conversas comerciais e oportunidades no CRM.",
        keywords: "campanha WhatsApp, marketing WhatsApp, templates de marketing WhatsApp",
      },
    ],
  },
  {
    id: "segmentos",
    title: "Segmentos atendidos",
    items: [
      {
        id: "pme",
        q: "A Kentro serve para pequenas e médias empresas?",
        a: "Sim. A Kentro foi pensada para empresas que precisam organizar atendimento, vendas e relacionamento sem criar uma operação complexa demais. Pequenas e médias empresas podem usar a Kentro para atender melhor no WhatsApp, centralizar canais, controlar leads, automatizar tarefas e crescer sem depender apenas de mais pessoas na equipe.",
        keywords: "CRM para PME, atendimento para pequenas empresas, WhatsApp para empresas",
      },
      {
        id: "quais-segmentos",
        q: "Quais segmentos podem usar a Kentro?",
        a: "A Kentro atende diversos segmentos, incluindo clínicas, farmácias, provedores de internet, escolas, faculdades, cursos, e-commerce, varejo, concessionárias, condomínios, lojas de informática, serviços e operações comerciais. A plataforma pode ser adaptada ao vocabulário, objeções, processos e fluxos de cada tipo de negócio.",
        keywords: "IA por segmento, atendimento para clínicas, CRM para provedores, WhatsApp para farmácias",
      },
      {
        id: "clinicas",
        q: "A Kentro serve para clínicas e consultórios?",
        a: "Sim. Clínicas e consultórios podem usar a Kentro para centralizar agendamentos, confirmar consultas, enviar lembretes, responder dúvidas frequentes, fazer pré-triagem, reduzir faltas e organizar o histórico do paciente. Isso ajuda a equipe a responder mais rápido e manter a agenda mais previsível.",
        keywords: "WhatsApp para clínicas, IA para clínicas, CRM para clínicas, agendamento por WhatsApp",
      },
      {
        id: "farmacias",
        q: "A Kentro serve para farmácias e drogarias?",
        a: "Sim. Farmácias, drogarias e farmácias de manipulação podem usar a Kentro para responder clientes no WhatsApp, organizar pedidos, confirmar disponibilidade, encaminhar receitas, lembrar recompra, segmentar clientes e melhorar o acompanhamento pós-venda. A IA pode ajudar a reduzir dúvidas repetitivas e acelerar o atendimento.",
        keywords: "WhatsApp para farmácias, CRM para farmácia, IA para farmácia",
      },
      {
        id: "provedores",
        q: "A Kentro serve para provedores de internet?",
        a: "Sim. Provedores de internet podem usar a Kentro para atendimento, suporte, triagem de chamados, cobrança automática, envio de boleto, segunda via pelo WhatsApp, SLA e organização do relacionamento com clientes. A Kentro informa resultados em provedores, como redução de custo de atendimento e inadimplência em cases medidos pela empresa.",
        keywords: "CRM para provedor de internet, WhatsApp para ISP, atendimento ISP",
      },
      {
        id: "educacao",
        q: "A Kentro serve para escolas, cursos e faculdades?",
        a: "Sim. Instituições de ensino podem usar a Kentro para captar leads, responder dúvidas sobre cursos, mensalidades e matrículas, qualificar interessados, organizar etapas comerciais e atender fora do horário comercial com IA. Isso ajuda a reduzir perda de leads e melhorar o acompanhamento até a matrícula.",
        keywords: "CRM para faculdades, WhatsApp para escolas, IA para educação",
      },
      {
        id: "ecommerce",
        q: "A Kentro serve para e-commerce e varejo?",
        a: "Sim. E-commerces e lojas de varejo podem usar a Kentro para responder dúvidas sobre produtos, frete, prazo de entrega, pedidos, carrinhos abandonados, trocas e pós-venda. A integração entre WhatsApp, CRM e IA ajuda a recuperar oportunidades e aumentar conversão sem depender apenas de atendimento manual.",
        keywords: "WhatsApp para e-commerce, atendimento para loja online, recuperar carrinho WhatsApp",
      },
      {
        id: "concessionarias",
        q: "A Kentro serve para concessionárias?",
        a: "Sim. Concessionárias podem usar a Kentro para qualificar leads, organizar interesse por veículo, distribuir contatos entre vendedores, agendar visitas e test drives, automatizar follow-ups e evitar que leads caros esfriem por demora no atendimento.",
        keywords: "CRM para concessionária, WhatsApp para concessionária, leads automotivos",
      },
    ],
  },
  {
    id: "operacao-resultados",
    title: "Operação e resultados",
    items: [
      {
        id: "tempo-de-resposta",
        q: "A Kentro melhora o tempo de resposta?",
        a: "Sim. Ao centralizar canais, automatizar triagens, distribuir conversas e usar IA para dúvidas comuns, a Kentro ajuda a reduzir o tempo de resposta e a evitar que leads fiquem parados. Isso é importante porque, em vendas pelo WhatsApp, demora no primeiro contato pode reduzir a chance de conversão.",
        keywords: "reduzir tempo de resposta, atendimento rápido WhatsApp, SLA atendimento",
      },
      {
        id: "reduzir-custos",
        q: "A Kentro ajuda a reduzir custos de atendimento?",
        a: "Sim. A Kentro ajuda a reduzir custos ao automatizar dúvidas repetitivas, organizar filas, melhorar produtividade da equipe, reduzir retrabalho e dar visibilidade sobre indicadores. Em cases por segmento, a Kentro apresenta reduções de custo e ganhos operacionais em áreas como provedores, clínicas, e-commerce e atendimento geral.",
        keywords: "reduzir custo de atendimento, produtividade atendimento, ROI atendimento",
      },
      {
        id: "relatorios-indicadores",
        q: "A Kentro tem relatórios e indicadores?",
        a: "Sim. A Kentro permite acompanhar indicadores de atendimento, vendas e operação, como volume de conversas, tempo de resposta, produtividade, canais de origem, etapas do funil, resolução, conversão e desempenho da equipe. Esses dados ajudam gestores a identificar gargalos e tomar decisões com base em informação real.",
        keywords: "indicadores de atendimento, dashboard atendimento, BI atendimento, métricas WhatsApp",
      },
      {
        id: "preciso-de-kentro",
        q: "Como saber se minha empresa precisa da Kentro?",
        a: "Sua empresa provavelmente precisa da Kentro se atende muitos clientes pelo WhatsApp, perde leads por demora, não tem histórico centralizado, usa vários canais separados, depende de celulares pessoais, não mede indicadores ou tem dificuldade para acompanhar vendas e suporte. A Kentro é indicada para operações que querem transformar atendimento em processo, dados e crescimento.",
        keywords: "preciso de CRM para WhatsApp, atendimento desorganizado, melhorar atendimento WhatsApp",
      },
    ],
  },
  {
    id: "seguranca-lgpd",
    title: "Segurança e LGPD",
    items: [
      {
        id: "kentro-segura",
        q: "A Kentro é segura?",
        a: "Sim. A Kentro trabalha com uma proposta de operação profissional, centralizada e alinhada à LGPD, ajudando empresas a reduzir riscos de conversas espalhadas em celulares pessoais, perda de histórico e falta de controle sobre dados. Para cada operação, é importante configurar permissões, usuários, políticas de acesso e boas práticas de segurança.",
        keywords: "atendimento seguro WhatsApp, LGPD atendimento, segurança CRM",
      },
      {
        id: "lgpd",
        q: "A Kentro está em conformidade com a LGPD?",
        a: "A Kentro apoia empresas na organização do atendimento e no controle de dados de clientes em um ambiente mais profissional. A plataforma ajuda a reduzir exposição de informações em dispositivos pessoais e centraliza histórico, permissões e processos. A conformidade com a LGPD também depende das políticas internas da empresa, bases legais, consentimentos e uso correto dos dados.",
        keywords: "LGPD WhatsApp, CRM LGPD, atendimento em conformidade",
      },
    ],
  },
  {
    id: "implementacao",
    title: "Implementação e suporte",
    items: [
      {
        id: "tempo-implantacao",
        q: "Quanto tempo leva para implantar a Kentro?",
        a: "O tempo de implantação depende do tamanho da operação, canais utilizados, integrações, quantidade de usuários e nível de personalização da IA. Em operações mais simples, a ativação pode ser rápida. Em projetos com IA treinada, integrações e fluxos específicos, o prazo pode variar conforme o volume de conteúdo e validações necessárias.",
        keywords: "implantação Kentro, configurar WhatsApp API, onboarding CRM",
      },
      {
        id: "equipe-tecnica",
        q: "Preciso de equipe técnica para usar a Kentro?",
        a: "Não para o uso diário. A Kentro foi pensada para equipes de atendimento, vendas, suporte e gestão. Para integrações nativas, a configuração costuma ser mais simples. Para integrações personalizadas com sistemas internos, pode ser necessário apoio técnico, mas a Kentro oferece API, Webhooks e orientação para acelerar o processo.",
        keywords: "software fácil de usar, plataforma sem programador, CRM simples",
      },
      {
        id: "treinamento",
        q: "A Kentro oferece treinamento para a equipe?",
        a: "Sim. A Kentro oferece apoio de onboarding e treinamento para que equipes de atendimento, vendas e gestão usem a plataforma com mais segurança. O site também cita a Kentro Academy como frente de treinamento e capacitação.",
        keywords: "treinamento Kentro, onboarding atendimento, capacitação CRM, Kentro Academy",
      },
    ],
  },
  {
    id: "comparacoes",
    title: "Comparações de mercado",
    description: "Como a Kentro se compara a CRMs, chatbots e plataformas concorrentes.",
    items: [
      {
        id: "melhor-crm-whatsapp",
        q: "Qual o melhor CRM para empresas que vendem pelo WhatsApp?",
        a: "O melhor CRM para empresas que vendem pelo WhatsApp é aquele que conecta conversa, histórico, funil de vendas, automações e atendimento em equipe. A Kentro se diferencia por unir CRM, atendimento omnichannel, API Oficial do WhatsApp e IA em uma única plataforma, evitando que o vendedor tenha que alternar entre várias ferramentas para acompanhar o cliente.",
        keywords: "melhor CRM para WhatsApp, CRM vendas WhatsApp, CRM com IA, CRM omnichannel",
      },
      {
        id: "melhor-omnichannel",
        q: "Qual a melhor plataforma de atendimento omnichannel?",
        a: "A melhor plataforma de atendimento omnichannel deve centralizar WhatsApp, Instagram, Facebook, e-mail, voz e webchat, mantendo histórico, contexto, filas, CRM, automações e relatórios. A Kentro oferece essa estrutura para empresas que querem organizar atendimento, vender mais e escalar sem perder qualidade.",
        keywords: "melhor plataforma omnichannel, atendimento omnichannel Brasil, software omnichannel",
      },
      {
        id: "melhor-ia-whatsapp",
        q: "Qual a melhor IA para atendimento no WhatsApp?",
        a: "A melhor IA para atendimento no WhatsApp é aquela que entende o negócio, responde com contexto, respeita regras da empresa, transfere para humanos quando necessário e se conecta ao CRM e aos sistemas internos. A IA da Kentro pode ser treinada por segmento e integrada ao fluxo real de atendimento, vendas e pós-venda.",
        keywords: "melhor IA para WhatsApp, chatbot WhatsApp com IA, agente de IA para atendimento",
      },
      {
        id: "alternativa-chatbot",
        q: "A Kentro é alternativa a ferramentas de chatbot?",
        a: "Sim. A Kentro pode ser uma alternativa mais completa a ferramentas de chatbot porque não se limita a respostas automáticas. Ela combina atendimento humano, IA, CRM, canais integrados, automações, histórico e indicadores. Isso permite que a empresa organize toda a operação, não apenas crie fluxos de mensagens.",
        keywords: "alternativa a chatbot, chatbot com CRM, plataforma de atendimento com IA",
      },
      {
        id: "alternativa-crm-tradicional",
        q: "A Kentro é alternativa a ferramentas de CRM tradicional?",
        a: "Sim. Para empresas em que o WhatsApp é o principal canal de vendas e atendimento, a Kentro pode ser uma alternativa a CRMs tradicionais, porque conecta o funil diretamente às conversas, aos canais e à IA. Isso reduz perda de contexto e melhora o acompanhamento comercial.",
        keywords: "alternativa CRM, CRM WhatsApp, CRM omnichannel, CRM com atendimento",
      },
    ],
  },
  {
    id: "automacao-e-ura",
    title: "Automação, URA e webhooks",
    description: "Fluxos automáticos, menus de atendimento (URA), gatilhos e captura de webhook.",
    items: [
      {
        id: "ura-whatsapp",
        q: "A Kentro tem URA no WhatsApp?",
        a: "Sim. A Kentro possui um construtor visual de fluxos automáticos e URA (menu de atendimento). Você monta o caminho do cliente com opções (“digite 1 para agendar, 2 para financeiro…”), coleta de dados e direcionamento por fila — funciona nos canais de chat, antes ou em vez do atendimento humano.",
        keywords: "URA WhatsApp, menu automático, fluxo de atendimento, construtor visual",
      },
      {
        id: "sem-programar-automacoes",
        q: "Preciso saber programar para criar automações na Kentro?",
        a: "Não. O construtor de fluxos e URA é visual, e os gatilhos são configurados por regras e condições diretamente na interface — sem código. Isso vale para automações do CRM (mover oportunidades, atualizar contatos, criar tarefas), gatilhos de atendimento e gatilhos de mensagem.",
        keywords: "automação sem código, no-code, criar fluxo sem programar",
      },
      {
        id: "gatilho-atendimento-vs-mensagem",
        q: "Qual a diferença entre gatilho de atendimento e gatilho de mensagem?",
        a: "O gatilho de atendimento reage a condições do próprio atendimento (tempo aguardando, status, motivo de encerramento). O gatilho de mensagem reage ao conteúdo enviado pelo cliente — por exemplo, quem escreve “2ª via” recebe o boleto na hora, ou quem escreve “cancelar” é roteado direto para a fila de retenção com prioridade.",
        keywords: "gatilho de atendimento, gatilho de mensagem, automação por palavra-chave",
      },
      {
        id: "captura-webhook",
        q: "Consigo receber leads do meu site ou do Facebook Lead Ads?",
        a: "Sim, pela Captura de Webhook. Cada webhook tem uma chave própria: o formulário do seu site, o lead ad do Facebook ou qualquer sistema externo envia os dados para a URL da Kentro. A plataforma cria automaticamente o contato, abre a oportunidade no funil e dispara a primeira mensagem — atendimento iniciado em segundos.",
        keywords: "captura de webhook, integrar formulário do site, Facebook Lead Ads WhatsApp",
      },
    ],
  },
  {
    id: "campanhas-e-templates",
    title: "Campanhas e templates",
    description: "Disparos em massa profissionais pela API Oficial do WhatsApp.",
    items: [
      {
        id: "campanhas-disparo-massa",
        q: "Como faço campanhas de disparo em massa sem risco de bloqueio?",
        a: "Em Configurações > Campanhas você escolhe um template WA Cloud API aprovado pela Meta, seleciona a fila de disparo e o público (contatos da Agenda) e acompanha a contagem de envios e o estado da campanha em tempo real. Como usa a API Oficial com templates aprovados, os disparos seguem as regras do WhatsApp — é o caminho seguro e profissional para envio em escala.",
        keywords: "campanha WhatsApp, disparo em massa seguro, templates Meta, WA Cloud API",
      },
      {
        id: "quem-responde-campanha",
        q: "Quem responde à minha campanha cai em qual atendimento?",
        a: "Na fila de atendimento vinculada ao disparo. A partir dali, a conversa pode continuar com um agente humano, passar por URA ou ser atendida por um assistente de IA — mantendo todo o histórico do contato.",
        keywords: "resposta de campanha, roteamento de campanha, follow-up campanha",
      },
      {
        id: "diferenca-msg-predefinida-template",
        q: "Qual a diferença entre mensagem pré-definida e template WA Cloud API?",
        a: "Mensagem pré-definida é uma resposta rápida usada pelo agente dentro de uma conversa em andamento (atalho para respostas frequentes). Template WA Cloud API é um modelo aprovado pela Meta, exigido para mensagens ativas ou disparos em massa pela API Oficial do WhatsApp.",
        keywords: "mensagem pré-definida, template WhatsApp, resposta rápida",
      },
    ],
  },
  {
    id: "tickets-e-sla",
    title: "Tickets, SLA e suporte estruturado",
    description: "Para operações B2B, técnicas ou com SLA formal.",
    items: [
      {
        id: "chat-vs-ticket",
        q: "Qual a diferença entre atendimento (chat) e ticket na Kentro?",
        a: "O chat é a conversa em tempo real com o cliente. O ticket é a demanda estruturada — com número, classificação, responsável, estágios (ex.: Aberto → Em análise → Aguardando cliente → Resolvido) e SLA. Um atendimento pode gerar um ticket quando a solução não é imediata, e tickets também podem nascer de e-mails vinculados ao módulo.",
        keywords: "chat vs ticket, sistema de tickets, suporte B2B, help desk",
      },
      {
        id: "sla-kentro",
        q: "A Kentro controla SLA?",
        a: "Sim. O Dashboard de Tickets acompanha o SLA por cliente/empresa, e o Relatório de Filas exibe SLA e tempos médios por canal. Também é possível definir prazos-alvo por plano de cliente e priorizar automaticamente os tickets próximos de vencer.",
        keywords: "SLA WhatsApp, dashboard de tickets, prazo de atendimento",
      },
      {
        id: "protocolo-atendimento",
        q: "Como encontro uma conversa antiga específica?",
        a: "Em Relatórios > Histórico de Atendimentos, filtre pelo número do cliente ou pelo protocolo do atendimento (número único que identifica cada conversa). Isso resolve rapidamente discussões sobre “o que foi combinado”, com data, hora e conteúdo exato.",
        keywords: "protocolo de atendimento, histórico de conversas, buscar atendimento antigo",
      },
    ],
  },
  {
    id: "produtividade-e-governanca",
    title: "Produtividade, permissões e auditoria",
    items: [
      {
        id: "grupos-de-acesso",
        q: "Como controlo o que cada usuário acessa na Kentro?",
        a: "Pelas permissões individuais e pelos Grupos de Acesso — perfis de permissão reutilizáveis (ex.: Agente, Supervisor, Administrador). Você define o perfil uma vez e aplica a todos os usuários dessa função, o que dá segurança e padronização conforme a equipe cresce.",
        keywords: "permissões, grupos de acesso, controle de usuários, governança",
      },
      {
        id: "pausas-agente",
        q: "Como funcionam as pausas dos agentes?",
        a: "Em Configurações > Pausas você cadastra motivos de pausa (almoço, café, treinamento) com tempo máximo, quantidade de vezes por dia e ação associada. Agente em pausa não recebe novas conversas até voltar ao estado disponível, e o Relatório de Tempos mostra se as regras estão sendo cumpridas — sem microgerenciamento.",
        keywords: "pausas de agente, motivo de pausa, relatório de tempos",
      },
      {
        id: "log-de-auditoria",
        q: "A Kentro registra o que cada usuário faz?",
        a: "Sim. O Log de Auditoria (Configurações > Log de Auditoria) registra todas as ações realizadas na plataforma — quem alterou uma configuração de fila, quem excluiu um contato, quem mexeu em uma automação. Rastreabilidade completa para segurança e conformidade.",
        keywords: "log de auditoria, rastreabilidade, quem alterou, LGPD",
      },
      {
        id: "monitoramento-produtividade",
        q: "A Kentro tem monitoramento de produtividade da equipe?",
        a: "Sim. O módulo de Monitoramento de Produtividade permite cadastrar aplicações, definir regras de matching (o que é produtivo ou improdutivo) e aplicar perfis de scoring por função. Isso dá visibilidade objetiva de como o tempo de trabalho é usado — adequado a equipes presenciais e remotas.",
        keywords: "monitoramento produtividade, scoring, aplicações, home office",
      },
      {
        id: "kentro-academy",
        q: "Onde a equipe aprende a usar a Kentro?",
        a: "Na Kentro Academy (academy.kentro.com.br), a plataforma oficial de treinamento da Kentro, com cursos para agentes, gerência e diretoria. A empresa também conta com apoio de onboarding e suporte do time Kentro durante a implantação.",
        keywords: "Kentro Academy, treinamento Kentro, capacitação, onboarding",
      },
    ],
  },
];

export const ALL_FAQS: FaqItem[] = FAQ_GROUPS.flatMap((g) => g.items);
